import {
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
  Paper,
  Checkbox,
  FormControlLabel,
  Link,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import axios from "../api/axios";
import { useNavigate, useLocation } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import "./LoginPage.css";
import useAuth from "../hooks/useAuth";

import logo from "../assets/favicon.png";
import bgImage from "../assets/Group.svg";

const LoginPage = () => {
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { setAuth } = useAuth();
  const [ssoProviders, setSsoProviders] = useState([]);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const [enteredNameIsTouched, setEnteredNameIsTouched] = useState(false);
  const [enteredPasswordIsTouched, setEnteredPasswordIsTouched] =
    useState(false);

  const enteredNameIsValid = userName.trim() !== "";
  const enteredPasswordIsValid = password.length > 3;
  const nameInputIsInvalid = !enteredNameIsValid && enteredNameIsTouched;
  const passwordInputIsInvalid =
    !enteredPasswordIsValid && enteredPasswordIsTouched;

  const userRef = useRef();
  const passwordRef = useRef();
  const navigate = useNavigate();

  const enabled = userName.length > 0 && password.length > 0;

  const LOGIN_URL = "api/auth/v1/login";

  const location = useLocation();
  const from = location.state?.from?.pathname || "/home";

  useEffect(() => {
    if (localStorage.getItem("user_name")) {
      navigate(from, { replace: true });
    }
    userRef.current?.focus();
    checkSetupStatus();
    fetchSSOStatus();
  }, [navigate, from]);

  const checkSetupStatus = async () => {
    try {
      const response = await axios.get("api/setup/status");
      if (response.data && response.data.is_setup === false) {
        navigate("/setup");
      }
    } catch (err) {
      console.error("Error checking setup status:", err);
    }
  };

  const fetchSSOStatus = async () => {
    try {
      const response = await axios.get("api/auth/v1/sso/status");
      setSsoProviders(response.data.providers || []);
    } catch (err) {
      console.error("Error fetching SSO status:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEnteredNameIsTouched(true);
    setEnteredPasswordIsTouched(true);

    if (!enteredNameIsValid || !enteredPasswordIsValid) {
      return;
    }

    if (isLoggingIn) return;
    setIsLoggingIn(true);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    try {
      const response = await axios.post(
        LOGIN_URL,
        JSON.stringify({ username: userName, password: password }),
        {
          headers: {
            "Content-Type": "application/json",
            "Time-Zone": `${timeZone}`,
          },
          withCredentials: false,
        }
      );
      const access_token = response?.data?.access_token;
      const roles = response?.data?.role;
      const refresh_token = response?.data?.refresh_token;
      const user_name = response?.data?.user_name;
      setAuth({ user_name, password, roles, access_token, refresh_token });
      setUserName("");
      setPassword("");
      if (roles === "admin" || roles === "superadmin" || roles === "member") {
        navigate(from, { replace: true });
      } else {
        navigate("/");
      }
      return {
        access_token: window.localStorage.setItem("access_token", access_token),
        refresh_token: window.localStorage.setItem(
          "refresh_token",
          refresh_token
        ),
        user_name: window.localStorage.setItem("user_name", user_name),
        roles : window.localStorage.setItem("roles", roles),
      };
    } catch (err) {
      setErrorMessage(err?.response?.data?.message);
      setShowError(true);
      console.log(err);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleSSOLogin = async (provider) => {
    try {
      const redirect_uri = window.location.origin + `/sso/callback/${provider}`;
      const response = await axios.get(`api/auth/v1/sso/init/${provider}?redirect_uri=${encodeURIComponent(redirect_uri)}`);
      if (response.data.login_url) {
        window.location.href = response.data.login_url;
      }
    } catch (err) {
      console.error(`${provider} Login Error:`, err);
      setErrorMessage(`${provider} authentication is not configured yet.`);
      setShowError(true);
    }
  };

  const hideShowPassword = () => setShowPassword((s) => !s);

  return (
    <div className="login-shell" style={{ fontFamily: "Work Sans" }}>
      <div className="brand-pane">
        <div className="brand-header">
          <img src={logo} alt="Yottaasys" className="brand-logo" />
          <span className="brand-name">Yottaasys</span>
        </div>

        <h1 className="hero-title">AI-Powered Defect Detection</h1>
        <p className="hero-subtitle">
          Enhancing Quality Control with Precision
        </p>

        <div
          className="hero-illustration"
          style={{ backgroundImage: `url(${bgImage})` }}
          aria-hidden
        />
      </div>

      <div className="form-pane">
        <Paper
          elevation={0}
          className="form-card"
          style={{ backgroundColor: "transparent" }}
        >
          <h2 className="form-title">Sign In to Your Account</h2>

          <form
            onSubmit={handleSubmit}
            onKeyDown={(e) => {
              if (e.key === "Enter" && isLoggingIn) {
                e.preventDefault();
              }
            }}
            noValidate
          >
            <div className="field">
              <TextField
                disabled={isLoggingIn}
                fullWidth
                inputRef={userRef}
                id="email"
                label="Email"
                placeholder="Enter your email"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                onFocus={() => setShowError(false)}
                error={nameInputIsInvalid}
                helperText={
                  nameInputIsInvalid ? "Email must not be empty." : " "
                }
                variant="outlined"
                margin="normal"
                slotProps={{
                  inputLabel: { shrink: true },
                }}
              />
            </div>

            <div className="field">
              <TextField
                disabled={isLoggingIn}
                inputRef={passwordRef}
                fullWidth
                id="password"
                label="Password"
                placeholder="Enter your password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setShowError(false)}
                error={passwordInputIsInvalid}
                helperText={
                  passwordInputIsInvalid
                    ? "Password length should be more than 4."
                    : " "
                }
                variant="outlined"
                margin="normal"
                slotProps={{
                  inputLabel: { shrink: true },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </div>

            {showError && <p className="error global-error">{errorMessage}</p>}

            <div
              // className="options-row"
              style={{ textAlign: "end" }}
            >
              {/* <FormControlLabel
                control={<Checkbox size="small" />}
                label="Remember Me"
              /> */}
              <Link
                component="button"
                type="button"
                className="forgot-link"
                onClick={() => navigate("forgot-password")}
                underline="none"
              >
                Forgot your password?
              </Link>
            </div>

            <Button
              type="submit"
              disabled={isLoggingIn}
              variant="contained"
              className="login-cta"
            >
              {isLoggingIn ? "Logging in..." : "Login"}
            </Button>

            {ssoProviders.length > 0 && (
              <>
                <div className="sso-divider">Or continue with</div>
                <div className="sso-buttons">
                  {ssoProviders.includes('azure') && (
                    <Button
                      variant="outlined"
                      className="sso-button"
                      onClick={() => handleSSOLogin('azure')}
                      startIcon={<img src="https://authjs.dev/img/providers/azure.svg" alt="Microsoft" style={{width: 20}} />}
                    >
                      Sign in with Microsoft
                    </Button>
                  )}
                  {ssoProviders.includes('google') && (
                    <Button
                      variant="outlined"
                      className="sso-button"
                      onClick={() => handleSSOLogin('google')}
                      startIcon={<img src="https://authjs.dev/img/providers/google.svg" alt="Google" style={{width: 20}} />}
                    >
                      Sign in with Google
                    </Button>
                  )}
                </div>
              </>
            )}
          </form>
        </Paper>
      </div>
    </div>
  );
};

export default LoginPage;
